"use client";

import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { MermaidRenderer } from "./mermaid-renderer";
import { useReportReadingMode } from "./report-reading-mode";
import { readableReportParagraphs, readableReportLists, normalizeReportCopy } from '@/lib/report-paragraphs';

// LLM이 삽입할 수 있는 raw HTML 구조 화이트리스트
// (.kpi-grid / .news-list / .verdict / .cf-card / .chart-card / .bars / .seg-table + SVG Sankey)
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    // 리포트 HTML 블록 태그 (SVG는 Sankey 용)
    "svg", "rect", "path", "text", "polyline", "circle", "line", "g", "defs",
    // 기술적 분석 접이식 (details/summary)
    "details", "summary",
  ],
  attributes: {
    ...defaultSchema.attributes,
    // 모든 태그에서 class 허용 (스타일링용)
    "*": [
      ...((defaultSchema.attributes && defaultSchema.attributes["*"]) || []),
      "className", "class", "data-locked",
      // style은 width/height/color/background 정도만 — 구조적 인라인 스타일 허용
      ["style", /^(?:width|height|background|color|top|left|right|bottom|margin|padding|font-size|font-weight|text-align|opacity|transform):\s*[^;]+;?\s*/i],
    ],
    div: ["className", "class", "style", "id", "aria-label"],
    span: ["className", "class", "style"],
    a: ["className", "class", "href", "target", "rel", "title", "style"],
    img: ["src", "alt", "width", "height", "className", "class", "style"],
    // SVG 속성
    svg: ["viewBox", "xmlns", "preserveAspectRatio", "role", "aria-label", "width", "height", "class", "className"],
    rect: ["x", "y", "width", "height", "rx", "ry", "fill", "stroke", "class", "className"],
    path: ["d", "fill", "stroke", "stroke-width", "stroke-linecap", "stroke-linejoin", "class", "className"],
    text: ["x", "y", "class", "className", "text-anchor", "style"],
    polyline: ["points", "fill", "stroke", "stroke-width", "class", "className"],
    circle: ["cx", "cy", "r", "fill", "stroke", "class", "className"],
    line: ["x1", "y1", "x2", "y2", "stroke", "stroke-width", "class", "className"],
    g: ["class", "className", "transform"],
    button: ["className", "class", "type", "disabled"],
    details: ["className", "class", "open"],
    summary: ["className", "class"],
  },
  // script · iframe · object · embed · form · input 등 위험 태그는 기본 schema에서 이미 제외됨
  // 추가로 제거할 태그
  strip: ["script", "style", "iframe", "object", "embed", "form", "input", "link", "meta", "base"],
  // 프로토콜 필터 — javascript:, data:(텍스트), vbscript: 등 차단
  protocols: {
    href: ["http", "https", "mailto", "#"],
    src: ["http", "https"],
  },
};

export interface FactCheckParagraph {
  idx: number;
  excerpt: string;
  level: "fact" | "analysis" | "forecast";
  rationale: string;
  sourceRefs: number[];
}
export interface FactCheckData {
  paragraphs: FactCheckParagraph[];
  overall?: { factRatio: number; avgConfidence: "high" | "medium" | "low" };
}

interface MarkdownRendererProps {
  sentenceLists?: boolean;
  content: string;
  scrollTables?: boolean;
  className?: string;
  /** Fact-check 결과 — 있으면 각 문단에 evidence 배지 렌더 */
  factCheck?: FactCheckData | null;
}

/**
 * 텍스트 내 `[N]` 또는 `[N, M]` 패턴을 `.cite` 링크로 치환.
 * 예: "매출 $130.5B[1]. CUDA 생태계[2,3]."
 */
function renderCites(text: string): React.ReactNode {
  if (!text) return text;
  const parts = text.split(/(\[\d+(?:\s*,\s*\d+)*\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[(\d+(?:\s*,\s*\d+)*)\]$/);
    if (!m) return part;
    const nums = m[1].split(",").map((s) => s.trim());
    return (
      <span key={i}>
        {nums.map((n, j) => (
          <a key={j} className="cite" href={`#src-${n}`}>
            [{n}]
          </a>
        ))}
      </span>
    );
  });
}

/**
 * children(React 노드 배열)을 순회하면서 문자열만 renderCites 처리.
 */
function processChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === "string") return renderCites(children);
  if (Array.isArray(children)) {
    return children.map((c, i) =>
      typeof c === "string" ? <span key={i}>{renderCites(c)}</span> : c
    );
  }
  return children;
}

/**
 * Bullstory 리포트 본문용 마크다운 렌더러.
 * - 클래스 적용 없이 semantic HTML만 출력 → 상위의 .sec-body / .report-main CSS가 스타일링
 * - rehypeRaw로 LLM이 삽입한 <div class="kpi-grid">, <div class="verdict"> 등 raw HTML 통과
 * - 본문에 `[N]` 패턴을 자동으로 `.cite` 링크로 변환
 */
export function MarkdownRenderer({ content, className = "", scrollTables = false, sentenceLists = false }: MarkdownRendererProps) {
  const continuous = useReportReadingMode() === "continuous";
  // factCheck prop 은 타입 호환 위해 유지하나 UI 렌더 안 함 (사용자 가치 낮음 결론)
  // 필요 시 admin 품질 지표로 backend 에서만 활용
  return (
    <div className={`md-body ${className}`}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], ...(continuous ? [sentenceLists?readableReportLists:readableReportParagraphs] : [])]}
        remarkPlugins={[remarkGfm]}
        components={{
          details: ({children, className, open}) => continuous ? <section className={className}>{children}</section> : <details className={className} open={open}>{children}</details>,
          summary: ({children, className}) => continuous ? <div className={`report-detail-heading ${className || ""}`} role="heading" aria-level={4}>{children}</div> : <summary className={className}>{children}</summary>,
          span: ({children, className, style}) => continuous && className?.split(/\s+/).includes("tf-toggle") ? null : <span style={style} className={className}>{children}</span>,
          table: ({children}) => scrollTables ? <div className="md-table-scroll" tabIndex={0} role="region" aria-label="분석 데이터 표"><table>{children}</table></div> : <table>{children}</table>,
          p: ({ children }) => <p>{processChildren(children)}</p>,
          li: ({ children }) => <li>{processChildren(children)}</li>,
          strong: ({ children }) => <b>{children}</b>,
          code: ({ className: codeClassName, children }) => {
            const isInline = !codeClassName;
            if (codeClassName === "language-mermaid") {
              const mermaidCode = String(children).replace(/\n$/, "");
              return <MermaidRenderer code={mermaidCode} />;
            }
            if (isInline) return <code className="md-code-inline">{children}</code>;
            return (
              <code className={`md-code-block ${codeClassName || ""}`}>{children}</code>
            );
          },
          a: ({ href, children }) => {
            // `[N]` 형태의 링크 텍스트는 cite 스타일로
            const text = typeof children === "string" ? children : "";
            const isCite = /^\[\d+\]$/.test(text);
            const label = continuous && href === text && /^https?:\/\//.test(text) && URL.canParse(text)
              ? `${new URL(text).hostname.replace(/^www\./,'')} 원문 보기` : children;
            return (
              <a
                href={href}
                target={isCite ? undefined : "_blank"}
                rel="noopener noreferrer"
                className={isCite ? "cite" : "md-link"}
              >
                {label}
              </a>
            );
          },
          img: ({ src, alt }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={src}
              alt={alt || ""}
              className="md-img"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ),
        }}
      >
        {normalizeReportCopy(content)}
      </ReactMarkdown>
    </div>
  );
}
